import React, { useEffect, useRef, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { api, API, fileUrl } from "@/lib/api";
import { UploadCloud, Loader2, Sparkles, Save, Download, Image as ImageIcon, FileText } from "lucide-react";

export default function AdminRebrand() {
  const [settings, setSettings] = useState({
    brand_name: "Heda Plywoods",
    logo_horizontal_color_file_id: null,
    logo_horizontal_white_file_id: null,
    logo_vertical_color_file_id: null,
    logo_vertical_white_file_id: null,
    contact_lines: [],
  });
  const [logoFiles, setLogoFiles] = useState({
    horizontal_color: null,
    horizontal_white: null,
    vertical_color: null,
    vertical_white: null,
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [rebranding, setRebranding] = useState(false);
  const [progress, setProgress] = useState({ pages_done: 0, total_pages: 0, status: "", uploaded_pct: 0 });
  const [msg, setMsg] = useState("");
  const [previewKey, setPreviewKey] = useState(0);

  useEffect(() => {
    api.get("/brand-settings").then((r) => {
      setSettings({
        brand_name: r.data.brand_name || "Heda Plywoods",
        logo_horizontal_color_file_id: r.data.logo_horizontal_color_file_id || null,
        logo_horizontal_white_file_id: r.data.logo_horizontal_white_file_id || null,
        logo_vertical_color_file_id: r.data.logo_vertical_color_file_id || null,
        logo_vertical_white_file_id: r.data.logo_vertical_white_file_id || null,
        contact_lines: r.data.contact_lines || [],
      });
      setLogoFiles({
        horizontal_color: r.data.logo_horizontal_color_file || null,
        horizontal_white: r.data.logo_horizontal_white_file || null,
        vertical_color: r.data.logo_vertical_color_file || null,
        vertical_white: r.data.logo_vertical_white_file || null,
      });
    });
  }, []);

  const uploadLogo = async (file, slot) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", "image");
      const token = localStorage.getItem("heda_token");
      const res = await fetch(`${API}/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setLogoFiles((x) => ({ ...x, [slot]: data }));
      setSettings((s) => ({ ...s, [`logo_${slot}_file_id`]: data.id }));
      setPreviewKey((k) => k + 1); // logo changed → bust the preview cache
    } catch (e) {
      alert(`Upload failed: ${e.message}`);
    }
  };

  const save = async () => {
    setSavingSettings(true);
    try {
      const { data } = await api.put("/brand-settings", settings);
      setSettings(data);
      setMsg("Saved.");
      setPreviewKey((k) => k + 1); // contact/name changes → refresh preview
      setTimeout(() => setMsg(""), 2000);
    } catch (e) {
      alert(e?.response?.data?.detail || "Could not save");
    } finally {
      setSavingSettings(false);
    }
  };

  const rebrand = async (file) => {
    if (!file) return;
    setRebranding(true);
    setProgress({ pages_done: 0, total_pages: 0, status: "starting", uploaded_pct: 0 });
    try {
      const token = localStorage.getItem("heda_token");

      // ---------- 1. Init a chunked upload session ----------
      // We upload the PDF in ~4 MB chunks so Cloudflare / nginx body-size
      // limits never reject a big brochure. Each chunk is a small POST.
      const initFd = new FormData();
      initFd.append("filename", file.name);
      const initRes = await fetch(`${API}/rebrand-jobs/upload/init`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: initFd,
      });
      if (!initRes.ok) {
        const raw = await initRes.text();
        let detail = null;
        try { detail = JSON.parse(raw)?.detail; } catch { detail = raw.replace(/<[^>]+>/g, " ").trim().slice(0, 160); }
        throw new Error(detail || `Server returned ${initRes.status} on upload init`);
      }
      const { upload_id, chunk_size } = await initRes.json();

      // ---------- 2. Slice + upload each chunk ----------
      const totalChunks = Math.ceil(file.size / chunk_size);
      for (let i = 0; i < totalChunks; i++) {
        const slice = file.slice(i * chunk_size, (i + 1) * chunk_size);
        const fd = new FormData();
        fd.append("index", String(i));
        fd.append("chunk", slice, `chunk-${i}`);
        // Retry each chunk up to 3 times on transient network failures.
        let ok = false;
        let lastErr = null;
        for (let attempt = 0; attempt < 3 && !ok; attempt++) {
          try {
            const r = await fetch(`${API}/rebrand-jobs/upload/${upload_id}/chunk`, {
              method: "POST",
              headers: { Authorization: `Bearer ${token}` },
              body: fd,
            });
            if (!r.ok) {
              const raw = await r.text();
              let detail = null;
              try { detail = JSON.parse(raw)?.detail; } catch { detail = raw.replace(/<[^>]+>/g, " ").trim().slice(0, 160); }
              throw new Error(detail || `Chunk ${i} failed with ${r.status}`);
            }
            ok = true;
          } catch (e) {
            lastErr = e;
            await new Promise((r) => setTimeout(r, 800 * (attempt + 1)));
          }
        }
        if (!ok) throw new Error(lastErr?.message || `Chunk ${i} failed after 3 retries`);
        setProgress((p) => ({
          ...p,
          status: "uploading",
          uploaded_pct: Math.round(((i + 1) / totalChunks) * 100),
        }));
      }

      // ---------- 3. Finalize → server assembles + kicks off the job ----------
      const finFd = new FormData();
      finFd.append("total_chunks", String(totalChunks));
      const finRes = await fetch(`${API}/rebrand-jobs/upload/${upload_id}/finalize`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: finFd,
      });
      if (!finRes.ok) {
        const raw = await finRes.text();
        let detail = null;
        try { detail = JSON.parse(raw)?.detail; } catch { detail = raw.replace(/<[^>]+>/g, " ").trim().slice(0, 160); }
        throw new Error(detail || `Server returned ${finRes.status} on finalize`);
      }
      const job = await finRes.json();
      setProgress({ pages_done: 0, total_pages: job.total_pages || 0, status: "queued", uploaded_pct: 100 });

      // ---------- 4. Poll for progress ----------
      const jobId = job.id;
      let done = false;
      let terminalError = null;
      let consecutivePollFails = 0;
      while (!done) {
        await new Promise((r) => setTimeout(r, 2000));
        try {
          const { data } = await api.get(`/rebrand-jobs/${jobId}`);
          consecutivePollFails = 0;
          setProgress({
            pages_done: data.pages_done || 0,
            total_pages: data.total_pages || job.total_pages || 0,
            status: data.status,
            uploaded_pct: 100,
          });
          if (data.status === "done") {
            done = true;
          } else if (data.status === "failed") {
            done = true;
            terminalError =
              data.error ||
              "The rebrand job failed on the server. Please try a smaller PDF or contact support.";
          }
        } catch (pollErr) {
          // Transient network hiccup — retry a few times, then give up loudly.
          consecutivePollFails += 1;
          console.warn("poll error", pollErr);
          if (consecutivePollFails >= 10) {
            done = true;
            terminalError =
              "Lost contact with the server while waiting for the rebrand to finish. Please check your connection and retry.";
          }
        }
      }
      if (terminalError) {
        // Bubble the failure up to the outer try/catch so the user actually sees it.
        throw new Error(terminalError);
      }

      // ---------- 5. Download ----------
      const dlRes = await fetch(`${API}/rebrand-jobs/${jobId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!dlRes.ok) throw new Error("Could not download the rebranded PDF");
      const blob = await dlRes.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = file.name.replace(/\.pdf$/i, "") + "-heda.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e) {
      alert(`Rebrand failed: ${e.message || e}`);
    } finally {
      setRebranding(false);
      setProgress({ pages_done: 0, total_pages: 0, status: "", uploaded_pct: 0 });
    }
  };

  return (
    <AdminLayout>
      <div className="mb-6">
        <div className="hed-overline">Brand kit</div>
        <h1 className="font-serif text-3xl md:text-4xl mt-1 tracking-tight">
          Turn any supplier PDF into a Heda PDF.
        </h1>
        <p className="text-sm text-[#5C5C54] mt-1.5 max-w-2xl">
          Upload your Heda logos + contact once. Then any supplier brochure you drop below is scanned by
          AI, the supplier&rsquo;s logos and contact details are replaced with yours, and a Heda-branded
          contact page is appended — ready to share with customers.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="hed-card p-6">
          <div className="hed-overline mb-4">Brand settings</div>
          <div className="space-y-4">
            <div>
              <label className="text-xs text-[#5C5C54] uppercase tracking-wider">Brand name</label>
              <input
                data-testid="brand-name-input"
                value={settings.brand_name}
                onChange={(e) => setSettings({ ...settings, brand_name: e.target.value })}
                className="w-full bg-transparent border-b border-[#E8E6DF] focus:border-[#B5953B] outline-none py-2 text-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <LogoUploader
                label="Horizontal · Color"
                file={logoFiles.horizontal_color}
                onChange={(f) => uploadLogo(f, "horizontal_color")}
                testId="logo-horizontal-color"
              />
              <LogoUploader
                label="Horizontal · White"
                file={logoFiles.horizontal_white}
                onChange={(f) => uploadLogo(f, "horizontal_white")}
                testId="logo-horizontal-white"
              />
              <LogoUploader
                label="Vertical · Color"
                file={logoFiles.vertical_color}
                onChange={(f) => uploadLogo(f, "vertical_color")}
                testId="logo-vertical-color"
              />
              <LogoUploader
                label="Vertical · White"
                file={logoFiles.vertical_white}
                onChange={(f) => uploadLogo(f, "vertical_white")}
                testId="logo-vertical-white"
              />
            </div>
            <p className="text-[11px] text-[#5C5C54] -mt-2">
              Upload transparent PNGs. AI samples each page&rsquo;s background brightness and
              picks the White logo on dark backgrounds, Color on light. Vertical variants are
              used for tall/portrait spaces (e.g. side panels).
            </p>
            <div>
              <label className="text-xs text-[#5C5C54] uppercase tracking-wider">Contact block (one line each)</label>
              <textarea
                data-testid="contact-lines-input"
                rows={5}
                placeholder="+91 98xxxxxxxx&#10;hello@hedaplywoods.com&#10;www.hedaplywoods.com&#10;Shop No. 12, Main Road, City"
                value={(settings.contact_lines || []).join("\n")}
                onChange={(e) => setSettings({ ...settings, contact_lines: e.target.value.split("\n") })}
                className="w-full bg-[#FAFAF8] border border-[#E8E6DF] focus:border-[#B5953B] rounded-sm p-3 text-sm outline-none resize-none font-mono"
              />
              <p className="text-[11px] text-[#5C5C54] mt-1">Appears on the final Heda page + wherever supplier contacts are detected.</p>
            </div>
            <div className="flex items-center gap-3 pt-2">
              <button
                onClick={save}
                disabled={savingSettings}
                data-testid="save-brand-settings"
                className="bg-[#1A1A18] hover:bg-[#2a2a26] disabled:opacity-60 text-white px-4 py-2.5 rounded-sm text-sm font-semibold inline-flex items-center gap-2"
              >
                {savingSettings ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save
              </button>
              {msg && <span className="text-xs text-[#5E6033]">{msg}</span>}
            </div>
          </div>
        </div>

        <BrandPagePreview settings={settings} refreshKey={previewKey} />

        <div className="hed-card p-6">
          <div className="hed-overline mb-4">Rebrand a supplier PDF</div>
          <label
            data-testid="rebrand-dropzone"
            className={`block border border-dashed cursor-pointer text-center py-14 px-4 rounded-sm transition-colors ${
              rebranding ? "opacity-70 cursor-wait border-[#B5953B] bg-[#FDF7E3]" : "border-[#E8E6DF] hover:bg-[#FAFAF8]"
            }`}
          >
            <input
              type="file"
              accept=".pdf,application/pdf"
              disabled={rebranding}
              className="hidden"
              onChange={(e) => rebrand(e.target.files?.[0])}
              data-testid="rebrand-file-input"
            />
            {rebranding ? (
              <div className="space-y-2.5" data-testid="rebrand-progress">
                <div className="text-sm inline-flex items-center gap-2 text-[#5E6033]">
                  <Loader2 size={14} className="animate-spin" />
                  {progress.status === "starting"
                    ? "Preparing upload…"
                    : progress.status === "uploading"
                    ? `Uploading PDF… ${progress.uploaded_pct}%`
                    : progress.status === "queued"
                    ? "Queuing job…"
                    : progress.status === "running"
                    ? `Rebranding page ${progress.pages_done} of ${progress.total_pages || "?"}…`
                    : progress.status === "done"
                    ? "Finishing up…"
                    : "Working…"}
                </div>
                {(progress.status === "uploading" || progress.total_pages > 0) && (
                  <div
                    className="w-64 h-1.5 bg-[#F2F0EA] rounded-full mx-auto overflow-hidden"
                    role="progressbar"
                    aria-valuenow={progress.status === "uploading" ? progress.uploaded_pct : progress.pages_done}
                    aria-valuemin={0}
                    aria-valuemax={progress.status === "uploading" ? 100 : progress.total_pages}
                    data-testid="rebrand-progress-bar"
                  >
                    <div
                      className="h-full bg-[#B5953B] transition-all"
                      style={{
                        width: `${
                          progress.status === "uploading"
                            ? progress.uploaded_pct
                            : Math.min(100, Math.round((progress.pages_done / Math.max(1, progress.total_pages)) * 100))
                        }%`,
                      }}
                    />
                  </div>
                )}
                <div className="text-[11px] text-[#5C5C54]">
                  Large PDFs are chunked into small pieces so any file size works.
                  You can leave this tab open.
                </div>
              </div>
            ) : (
              <>
                <UploadCloud size={30} strokeWidth={1.5} className="mx-auto text-[#5C5C54] mb-3" />
                <div className="text-sm font-medium">Drop supplier PDF or click to select</div>
                <div className="text-xs text-[#5C5C54] mt-1">
                  AI detects supplier logos + contacts → replaces with your Heda brand → downloads as PDF.
                </div>
              </>
            )}
          </label>
          <div className="mt-4 text-[11px] text-[#5C5C54] leading-relaxed space-y-1">
            <div className="flex items-start gap-1.5">
              <Sparkles size={11} strokeWidth={2} className="mt-0.5 flex-shrink-0 text-[#B5953B]" />
              Uses Gemini vision + PyMuPDF. Product photos, tech specs and layout are preserved.
            </div>
            <div className="flex items-start gap-1.5">
              <FileText size={11} strokeWidth={2} className="mt-0.5 flex-shrink-0 text-[#5E6033]" />
              A final Heda contact page is auto-added.
            </div>
            <div className="flex items-start gap-1.5">
              <Download size={11} strokeWidth={2} className="mt-0.5 flex-shrink-0 text-[#5E6033]" />
              Runs as a background job — no timeout on large PDFs.
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

function LogoUploader({ label, file, onChange, testId }) {
  const inputRef = useRef(null);
  return (
    <div>
      <label className="text-xs text-[#5C5C54] uppercase tracking-wider">{label}</label>
      <div
        onClick={() => inputRef.current?.click()}
        className="mt-1 aspect-[3/2] flex items-center justify-center bg-[#FAFAF8] border border-dashed border-[#E8E6DF] hover:border-[#B5953B] rounded-sm cursor-pointer overflow-hidden"
        data-testid={testId}
      >
        {file ? (
          <img src={fileUrl(file.storage_path)} alt={label} className="max-w-full max-h-full object-contain" />
        ) : (
          <div className="text-center">
            <ImageIcon size={20} className="mx-auto text-[#5C5C54] mb-1" strokeWidth={1.5} />
            <div className="text-[11px] text-[#5C5C54]">Click to upload</div>
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/svg+xml"
          className="hidden"
          onChange={(e) => onChange(e.target.files?.[0])}
        />
      </div>
    </div>
  );
}

/**
 * Live preview of the branded LAST PAGE. Fetches the server-rendered PNG
 * whenever `refreshKey` changes (bumped by parent after logo upload / save).
 * Lets the admin sanity-check the design language BEFORE spending time on a
 * large rebrand job.
 */
function BrandPagePreview({ settings, refreshKey }) {
  const [mode, setMode] = useState("light"); // "light" | "dark"
  const [src, setSrc] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const objectUrlRef = useRef(null);

  const hasAnyLogo = !!(
    settings.logo_horizontal_color_file_id ||
    settings.logo_horizontal_white_file_id ||
    settings.logo_vertical_color_file_id ||
    settings.logo_vertical_white_file_id
  );

  useEffect(() => {
    if (!hasAnyLogo) {
      setSrc(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem("heda_token");
        const res = await fetch(`${API}/brand-settings/preview.png?dark=${mode === "dark"}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          const raw = await res.text();
          let msg = `Preview failed (${res.status})`;
          try { msg = JSON.parse(raw)?.detail || msg; } catch { /* not JSON */ }
          throw new Error(msg);
        }
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        if (cancelled) {
          URL.revokeObjectURL(url);
          return;
        }
        if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = url;
        setSrc(url);
      } catch (e) {
        if (!cancelled) setError(e.message || String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [refreshKey, mode, hasAnyLogo]);

  useEffect(() => () => {
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
  }, []);

  return (
    <div className="hed-card p-6" data-testid="brand-preview">
      <div className="flex items-center justify-between mb-4">
        <div className="hed-overline">Brand page preview</div>
        <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider">
          <button
            data-testid="preview-mode-light"
            onClick={() => setMode("light")}
            className={`px-2.5 py-1 rounded-sm transition-colors ${
              mode === "light"
                ? "bg-[#1A1A18] text-white"
                : "border border-[#E8E6DF] text-[#5C5C54] hover:border-[#B5953B]"
            }`}
          >
            Color
          </button>
          <button
            data-testid="preview-mode-dark"
            onClick={() => setMode("dark")}
            className={`px-2.5 py-1 rounded-sm transition-colors ${
              mode === "dark"
                ? "bg-[#1A1A18] text-white"
                : "border border-[#E8E6DF] text-[#5C5C54] hover:border-[#B5953B]"
            }`}
          >
            White
          </button>
        </div>
      </div>
      {!hasAnyLogo ? (
        <div className="text-xs text-[#5C5C54] py-16 text-center border border-dashed border-[#E8E6DF] rounded-sm">
          Upload at least one logo to see a live preview of your Heda contact page.
        </div>
      ) : loading ? (
        <div className="text-xs text-[#5C5C54] py-16 text-center inline-flex items-center gap-2 justify-center w-full">
          <Loader2 size={12} className="animate-spin" /> Rendering preview…
        </div>
      ) : error ? (
        <div className="text-xs text-[#9E2A2B] py-4">{error}</div>
      ) : src ? (
        <div className="bg-[#FAFAF8] border border-[#E8E6DF] rounded-sm p-4 flex justify-center">
          <img
            src={src}
            alt="Heda brand page preview"
            data-testid="brand-preview-img"
            className="max-w-full h-auto shadow-sm"
            style={{ maxHeight: 600 }}
          />
        </div>
      ) : null}
      <div className="text-[11px] text-[#5C5C54] mt-3">
        This is the final page that gets appended to every rebranded catalog.
        Toggle between the Color logo (used on light backgrounds) and the White
        logo (used where the source has a dark background).
      </div>
    </div>
  );
}
